<template>
    <div class="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 min-h-screen font-body">
        <header class="bg-purple-600 text-white py-8 shadow-2xl">
            <div class="container mx-auto px-4">
                <div class="flex flex-col items-center text-center">
                    <div class="flex items-center gap-4 mb-2">
                        <i class="fas fa-ticket-alt text-5xl"></i>
                        <h1 class="text-4xl md:text-5xl font-black">Acompanhamento de Senhas</h1>
                    </div>
                    <p class="text-purple-200 text-lg font-semibold">{{ nomeLoja }}</p>
                </div>
            </div>
        </header>

        <main class="container mx-auto px-4 py-8">
            <div v-if="loading" class="flex flex-col items-center justify-center py-16 text-white">
                <svg class="animate-spin h-16 w-16 text-purple-300 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
                <span class="text-xl font-bold">Carregando senhas...</span>
            </div>

            <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div class="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-6 border-2 border-amber-400">
                    <div class="flex items-center gap-3 mb-6 pb-4 border-b-2 border-amber-400">
                        <i class="fas fa-clock text-4xl text-amber-400"></i>
                        <div>
                            <h2 class="text-3xl font-black text-white">PREPARANDO</h2>
                            <p class="text-amber-200 text-sm font-semibold">{{ senhasPreparando.length }} senha(s) em preparo</p>
                        </div>
                    </div>

                    <div v-if="senhasPreparando.length === 0" class="text-center py-12 text-white/60">
                        <i class="fas fa-check-circle text-6xl mb-4"></i>
                        <p class="text-xl font-bold">Nenhuma senha em preparo</p>
                    </div>

                    <div v-else class="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-2 custom-scrollbar">
                        <div v-for="senha in senhasPreparando" :key="senha.id" class="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-4 shadow-lg transform transition-all duration-300 hover:scale-105">
                            <div class="flex items-center justify-between">
                                <div class="bg-white rounded-lg px-5 py-2">
                                    <p class="text-xs font-bold text-amber-600 uppercase tracking-wider">Senha</p>
                                    <p class="text-4xl font-black text-amber-600">{{ senha.senha }}</p>
                                </div>
                                <div class="flex flex-col items-end gap-2">
                                    <div class="bg-white/20 rounded-lg px-3 py-1 backdrop-blur-sm">
                                        <p class="text-white font-bold text-sm">
                                            <i class="fas fa-user mr-1"></i>{{ senha.cliente_nome }}
                                        </p>
                                    </div>
                                    <div class="flex items-center gap-1 text-white text-xs">
                                        <i class="fas fa-clock"></i>
                                        <span>{{ formatarTempo(senha.created_at) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-6 border-2 border-green-400">
                    <div class="flex items-center gap-3 mb-6 pb-4 border-b-2 border-green-400">
                        <i class="fas fa-check-circle text-4xl text-green-400"></i>
                        <div>
                            <h2 class="text-3xl font-black text-white">PRONTOS</h2>
                            <p class="text-green-200 text-sm font-semibold">{{ senhasProntos.length }} senha(s) pronta(s)</p>
                        </div>
                    </div>

                    <div v-if="senhasProntos.length === 0" class="text-center py-12 text-white/60 mb-6">
                        <i class="fas fa-hourglass-half text-6xl mb-4"></i>
                        <p class="text-xl font-bold">Nenhuma senha pronta ainda</p>
                    </div>

                    <div v-else class="space-y-4 mb-8">
                        <div v-for="senha in senhasProntos" :key="senha.id" class="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 shadow-lg transform transition-all duration-300 hover:scale-105 animate-pulse-slow">
                            <div class="flex items-center justify-between">
                                <div class="bg-white rounded-lg px-5 py-2">
                                    <p class="text-xs font-bold text-green-600 uppercase tracking-wider">Senha</p>
                                    <p class="text-4xl font-black text-green-600">{{ senha.senha }}</p>
                                </div>
                                <div class="flex flex-col items-end gap-2">
                                    <div class="flex items-center gap-2">
                                        <i class="fas fa-bell text-2xl text-white animate-bounce"></i>
                                    </div>
                                    <div class="bg-white/20 rounded-lg px-3 py-1 backdrop-blur-sm">
                                        <p class="text-white font-bold text-sm">
                                            <i class="fas fa-user mr-1"></i>{{ senha.cliente_nome }}
                                        </p>
                                    </div>
                                    <div class="flex items-center gap-1 text-white text-xs">
                                        <i class="fas fa-clock"></i>
                                        <span>{{ formatarTempo(senha.created_at) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="senhasHistorico.length > 0" class="border-t-2 border-white/30 pt-6">
                        <div class="flex items-center gap-2 mb-4">
                            <i class="fas fa-history text-2xl text-blue-300"></i>
                            <h3 class="text-xl font-bold text-white">Últimas Entregas</h3>
                        </div>

                        <div class="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                            <div v-for="senha in senhasHistorico" :key="senha.id" class="bg-white/5 rounded-lg p-3 border border-white/20">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-3">
                                        <div class="bg-blue-500 rounded-lg px-3 py-1">
                                            <p class="text-xl font-black text-white">{{ senha.senha }}</p>
                                        </div>
                                        <div>
                                            <p class="text-white font-semibold text-sm">
                                                <i class="fas fa-user mr-1"></i>{{ senha.cliente_nome }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="text-right flex items-center gap-2">
                                        <i class="fas fa-check-double text-blue-400 text-lg"></i>
                                        <p class="text-white/60 text-xs">{{ formatarTempo(senha.created_at) }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>

        <footer class="bg-purple-950 text-white py-6 mt-12">
            <div class="container mx-auto px-4 text-center">
                <p class="text-purple-300 text-sm font-semibold">
                    Sistema de Acompanhamento • Atualização automática a cada 10 segundos
                </p>
            </div>
        </footer>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, onUnmounted } from 'vue';
    import { useRoute } from 'vue-router';
    import { storeToRefs } from "pinia";
    import { useSenhaStore } from '@/stores/senha';

    const route = useRoute();
    const senhaStore = useSenhaStore();
    const { senhas, nomeLoja } = storeToRefs(senhaStore);

    const loading = ref(true);
    const lojaId = ref(null);
    let intervalId = null;

    const senhasPreparando = computed(() => {
        return senhas.value
            .filter(senha => senha.situacao === 0)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });

    const senhasProntos = computed(() => {
        return senhas.value
            .filter(senha => senha.situacao === 1)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });

    const senhasHistorico = computed(() => {
        return senhas.value
            .filter(senha => senha.situacao === 2)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5);
    });

    onMounted(async () => {
        lojaId.value = route.params.lojaId || route.query.lojaId;

        if (!lojaId.value) {
            console.error('lojaId não fornecido');
            loading.value = false;
            return;
        }

        await carregarSenhas();

        intervalId = setInterval(carregarSenhas, 10000);
    });

    onUnmounted(() => {
        if (intervalId) {
            clearInterval(intervalId);
        }
    });

    async function carregarSenhas() {
        try {
            await senhaStore.listarSenhasPedido(lojaId.value);
        } catch (error) {
            console.error('Erro ao carregar senhas:', error);
        } finally {
            loading.value = false;
        }
    }

    function formatarTempo(data) {
        const agora = new Date();
        const dataItem = new Date(data);
        const diffMs = agora - dataItem;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Agora';
        if (diffMins === 1) return '1 min atrás';
        if (diffMins < 60) return `${diffMins} mins atrás`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours === 1) return '1 hora atrás';
        if (diffHours < 24) return `${diffHours} horas atrás`;

        return dataItem.toLocaleDateString('pt-BR');
    }
</script>

<style scoped>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

    .font-body {
        font-family: 'Inter', sans-serif;
    }

    .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
    }

    .custom-scrollbar::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 10px;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 10px;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
    }

    .animate-pulse-slow {
        animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes pulse-slow {
        0%, 100% {
            opacity: 1;
        }
        50% {
            opacity: 0.85;
        }
    }
</style>